(ns machtfit.employee-rules
  (:require [clojure.set :refer [intersection]]
            [machtfit.utils :as utils]))

(comment
  ;; a rule is a condition with a result
  {:condition {:some-key "some-value"
               :some-other-key "some-other-value"
               :yet-another-key "another-value"}
   :result "some-result"}

  ;; a rule can match or not match a subject-group, which looks like this:
  {:subject {:some-key "a-value"
             :some-other-key nil}
   ;; special key
   :count 45})

;; a rule matches a subject-group if all keys with non-nil values from the rule match the
;; corresponding keys in the subject-group

;; when a rule matches a subject-group, its count is added to the rule's result

(defn matches? [condition subject]
  (every? #(= true %) (for [[k v] condition
                            :when (not (nil? v))]
                        (= v (k subject)))))

(defn filter-by-managed-employees [items rule-key managed-employees]
  (cond->> items
    (some? managed-employees)
    (filterv
     (fn [item]
       (some
        (fn [admin-rule]
          (or
           (not (contains? (admin-rule :condition) rule-key))
           (= (-> admin-rule :condition rule-key) (:id item))))
        managed-employees)))))

(def ^:private plural {:company :companies
                       :place-of-business :places-of-business
                       :subsidiary :subsidiaries
                       :occupation-group :occupation-groups})

(defn filter-by-current-rule
  "items is a list of condition options (dicts of companies, pobs, subs, employee groups or occupation groups)
   rule-key is the type of condition options in items (:company, :places-of-business, :subsidiary, :employee-groups or
            :occupation-group)
   current-rule is the rule currently being edited
   filter-by-existing-employees? defines whether we are only checking if the company of the
                                 condition option is matching the selected company in the current
                                 rule (false) or if we check all conditions of the rule. For
                                 instance if a certain subsidiary option is valid in combination
                                 with the selected occupation group of the current rule.
  "
  [items rule-key current-rule filter-by-existing-employees?]
  (let [filter-conditions (if filter-by-existing-employees?
                            [:company :occupation-group :subsidiary :places-of-business :employee-groups]
                            [:company])
        condition-filter (fn [item]
                           (fn [condition]
                             (let [selected-value (-> current-rule :condition condition)
                                   condition-plural (plural condition)]
                               (or
                                (= condition rule-key)
                                (nil? selected-value)
                                (if (coll? selected-value)
                                  (seq (intersection (-> item condition set) selected-value))
                                  (contains? (-> item condition-plural set) selected-value))))))
        item-may-stay? (fn [item]
                         (or
                          (-> item :id nil?) ;; placeholder value
                          (every? (condition-filter item) filter-conditions)))]
    (filterv item-may-stay? items)))

(defn aggregate-rules [allowed-employees condition-to-aggregate new-key]
  (->> allowed-employees
       ;; Ensure condition to aggregate exists (when not defined then it is `nil` afterwards)
       (map #(update-in % [:condition condition-to-aggregate] identity))
       (group-by #(update % :condition dissoc condition-to-aggregate))
       (map (fn [[common rules]]
              (let [aggregated-values (set (map #(get-in % [:condition condition-to-aggregate]) rules))]
                (cond-> common
                  (and (utils/not-in? aggregated-values nil) (seq aggregated-values)) (assoc-in [:condition new-key] aggregated-values)))))
       (into [])))

(defn expand-rules [allowed-employees condition-to-expand new-key]
  (->> allowed-employees
       (map
        (fn [rule]
          (if (-> rule :condition condition-to-expand some?)
            (map (fn [condition] (assoc-in rule [:condition new-key] condition)) (-> rule :condition condition-to-expand))
            rule)))
       flatten
       (map #(update % :condition dissoc condition-to-expand))
       (into [])))

(defn is-subset?
  "
  test whether rule 'superset' is a superset to rule 'subset' in the sense
  that every employee that matches rule 'superset' does also match rule 'subset'.

  For equal rules this will return true. If this is not desired use is-strict-subset? instead.
  "
  [{sub :condition} {super :condition}]
  (= super (select-keys sub (keys super))))

(defn is-strict-subset? [subset superset]
  (and (is-subset? subset superset) (not= superset subset)))

(defn is-redundant? [rule employee-rules]
  (some #(is-strict-subset? rule %) employee-rules))

(defn is-duplicate? [rule index employee-rules]
  (->> employee-rules
       (take index)
       (some #(= rule %))))
